// ─── Iter187 — Inspection booking block ──────────────────────────────
// Sticky / inline / tabbed booking block for inspection-type appraisals.
// Persists via PATCH /api/appraisals/{id}/booking which also auto-
// flips the appraisal_status from "Appraisal Required" → "Appraisal
// Booked" so the existing calendar + status workflow lights up.
// Supports conflict detection (warns when the proposed slot overlaps
// another booking for the same inspector).
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CalendarClock, AlertTriangle, Trash2, Save, Loader2, Plane, StickyNote, User, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import Combobox from './Combobox';

const DEFAULT = {
  date: '', time: '',
  duration_min: 60,
  travel_before_min: 0,
  travel_after_min: 0,
  inspector: '',
  notes: '',
  needs_booking: true,
};

// Compact summary chip used when the card is in collapsed/sticky mode.
function BookingSummary({ booking }) {
  if (!booking || !booking.date) {
    return <span className="text-slate-500 text-sm italic">No booking yet</span>;
  }
  const end = (() => {
    if (!booking.time) return '';
    try {
      const [h, m] = booking.time.split(':').map(Number);
      const total = (h * 60 + m) + Number(booking.duration_min || 60);
      const eh = Math.floor(total / 60) % 24, em = total % 60;
      return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
    } catch { return ''; }
  })();
  return (
    <span className="flex flex-wrap items-center gap-2 text-sm">
      <strong className="text-slate-900">{booking.date}</strong>
      <span className="text-slate-600">{booking.time}{end ? `–${end}` : ''}</span>
      {booking.inspector && <span className="px-1.5 py-0.5 bg-slate-100 rounded text-xs text-slate-700"><User size={10} className="inline -mt-0.5 mr-0.5" />{booking.inspector}</span>}
      {(booking.travel_before_min > 0 || booking.travel_after_min > 0) && (
        <span className="px-1.5 py-0.5 bg-sky-50 text-sky-700 rounded text-[11px]">
          <Plane size={9} className="inline -mt-0.5 mr-0.5" />{booking.travel_before_min || 0}m / {booking.travel_after_min || 0}m
        </span>
      )}
    </span>
  );
}

export default function BookingCard({
  appraisalId,
  initialBooking,
  appraisers = [],
  onSaved,
  variant = 'sticky',   // 'sticky' (compact summary + expand) | 'full' (always-expanded tab)
}) {
  const { api } = useAuth();
  const [booking, setBooking] = useState({ ...DEFAULT, ...(initialBooking || {}) });
  const [expanded, setExpanded] = useState(variant === 'full');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const conflictTimer = useRef(null);

  useEffect(() => { setBooking({ ...DEFAULT, ...(initialBooking || {}) }); }, [initialBooking]);

  // Debounced conflict check whenever date/time/duration/inspector
  // change while the card is expanded — gives the operator near-
  // realtime "this overlaps Smith's 9am" warning.
  useEffect(() => {
    if (!expanded || !appraisalId || !booking.date || !booking.time) {
      setConflicts([]);
      return;
    }
    clearTimeout(conflictTimer.current);
    conflictTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/booking-conflicts`, {
          params: {
            date: booking.date, time: booking.time,
            duration_min: booking.duration_min,
            travel_before_min: booking.travel_before_min,
            travel_after_min:  booking.travel_after_min,
            inspector: booking.inspector || '',
          },
        });
        setConflicts(data?.conflicts || []);
      } catch {
        setConflicts([]);
      }
    }, 350);
    return () => clearTimeout(conflictTimer.current);
  }, [api, appraisalId, expanded, booking.date, booking.time, booking.duration_min,
      booking.travel_before_min, booking.travel_after_min, booking.inspector]);

  const save = useCallback(async (override = null) => {
    setSaving(true);
    try {
      const payload = override || booking;
      const { data } = await api.patch(`/appraisals/${appraisalId}/booking`, payload);
      toast.success(payload.date ? 'Booking saved' : 'Booking cleared');
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [api, appraisalId, booking, onSaved]);

  const clearBooking = useCallback(async () => {
    if (!window.confirm('Clear this booking? The appraisal status will revert to "Client Data" if it\'s still on "Appraisal Booked".')) return;
    const cleared = { ...DEFAULT, needs_booking: booking.needs_booking };
    setBooking(cleared);
    await save({ ...cleared, date: '', time: '' });
  }, [booking.needs_booking, save]);

  const set = (k, v) => setBooking(prev => ({ ...prev, [k]: v }));

  // ─── Collapsed/sticky shell ─────────────────────────────────────────
  if (variant === 'sticky' && !expanded) {
    return (
      <div data-testid="booking-card-sticky"
        className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
        <CalendarClock size={16} className="text-[#D35400] shrink-0" />
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Booking</span>
        <div className="flex-1 min-w-0 truncate"><BookingSummary booking={booking} /></div>
        <button data-testid="booking-card-expand"
          onClick={() => setExpanded(true)}
          className="text-xs font-semibold text-[#D35400] hover:underline flex items-center gap-1">
          {booking.date ? 'Edit' : 'Schedule'} <ChevronDown size={12} />
        </button>
      </div>
    );
  }

  // ─── Full editor ────────────────────────────────────────────────────
  const todayIso = new Date().toISOString().slice(0, 10);
  const dateBelowToday = booking.date && booking.date < todayIso;

  return (
    <div data-testid="booking-card-full"
      className="bg-white border border-slate-200 rounded-lg p-4 space-y-3 shadow-sm">
      <div className="flex items-center gap-2">
        <CalendarClock size={18} className="text-[#D35400]" />
        <h3 className="font-bold text-slate-900 font-[Manrope]">Inspection Booking</h3>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input data-testid="booking-needs-booking"
            type="checkbox" checked={booking.needs_booking}
            onChange={e => set('needs_booking', e.target.checked)}
            className="h-3.5 w-3.5"
          />
          This appraisal needs a booking
        </label>
        {variant === 'sticky' && (
          <button onClick={() => setExpanded(false)}
            className="text-slate-500 hover:text-slate-900 ml-1" title="Collapse">
            <ChevronUp size={16} />
          </button>
        )}
      </div>

      {!booking.needs_booking ? (
        <div className="text-sm text-slate-500 italic py-2">Booking disabled for this appraisal (e.g. Drive-By).</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Date</label>
              <input data-testid="booking-date"
                type="date" value={booking.date} onChange={e => set('date', e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              {dateBelowToday && (
                <div className="text-[10px] text-amber-700 mt-0.5">Past date — confirm intentional</div>
              )}
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Start</label>
              <input data-testid="booking-time"
                type="time" value={booking.time} onChange={e => set('time', e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Duration (min)</label>
              <input data-testid="booking-duration"
                type="number" min={15} max={480} step={15}
                value={booking.duration_min}
                onChange={e => set('duration_min', Number(e.target.value || 60))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Inspector</label>
              <Combobox
                value={booking.inspector || ''}
                onChange={v => set('inspector', v)}
                options={appraisers || []}
                placeholder="Pick or type…"
                testId="booking-inspector"
                allowCustom={true}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1 flex items-center gap-1">
                <Plane size={10} /> Travel before (min)
              </label>
              <input data-testid="booking-travel-before"
                type="number" min={0} max={240} step={5}
                value={booking.travel_before_min}
                onChange={e => set('travel_before_min', Number(e.target.value || 0))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1 flex items-center gap-1">
                <Plane size={10} className="transform rotate-180" /> Travel after (min)
              </label>
              <input data-testid="booking-travel-after"
                type="number" min={0} max={240} step={5}
                value={booking.travel_after_min}
                onChange={e => set('travel_after_min', Number(e.target.value || 0))}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div className="col-span-2 md:col-span-4">
              <label className="block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1 flex items-center gap-1">
                <StickyNote size={10} /> Notes (gate codes, dog warnings, "knock loudly", …)
              </label>
              <textarea data-testid="booking-notes"
                value={booking.notes} rows={2}
                onChange={e => set('notes', e.target.value)}
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm resize-y"
              />
            </div>
          </div>

          {conflicts.length > 0 && (
            <div data-testid="booking-conflicts"
              className="border border-amber-300 bg-amber-50 rounded p-2 text-xs space-y-1">
              <div className="flex items-center gap-1 font-bold text-amber-800">
                <AlertTriangle size={12} /> {conflicts.length} booking conflict{conflicts.length > 1 ? 's' : ''} for this inspector
              </div>
              {conflicts.slice(0, 4).map(c => (
                <div key={c.appraisal_id} className="text-amber-900 font-mono">
                  · A{c.appraisal_no || '?'} {c.client_name && `— ${c.client_name}`} · {c.time}–{c.end_time}
                </div>
              ))}
              {conflicts.length > 4 && <div className="text-amber-700 italic">+ {conflicts.length - 4} more</div>}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button data-testid="booking-save"
              onClick={() => save()}
              disabled={saving || !booking.date || !booking.time}
              className="flex items-center gap-1.5 bg-[#D35400] hover:bg-[#A04000] text-white px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-40"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save booking
            </button>
            {booking.date && (
              <button data-testid="booking-clear"
                onClick={clearBooking}
                disabled={saving}
                className="flex items-center gap-1.5 bg-white border border-rose-300 text-rose-700 hover:bg-rose-50 px-3 py-1.5 rounded text-sm font-semibold"
              >
                <Trash2 size={12} /> Clear
              </button>
            )}
            <div className="ml-auto text-[11px] text-slate-500 italic">
              Saving will flip status to <strong className="text-slate-700">Appraisal Booked</strong> automatically.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
